// src/pages/dropshipping/ResellerRequestsPage.jsx
import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Clock, ImageIcon } from 'lucide-react';
import { getImageUrl } from '../../utils/imageUtils';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { getIncomingRequests, reviewRequest } from '../../services/api/dropshipping';
import { formatDate } from '../../utils/dateUtils';
import styles from './ResellerRequestsPage.module.css';

const STATUS_CONFIG = {
  pending: { icon: Clock, className: 'statusPending' },
  approved: { icon: CheckCircle, className: 'statusApproved' },
  rejected: { icon: XCircle, className: 'statusRejected' },
};

const ResellerRequestsPage = () => {
  const { t } = useLocalization();
  const { success, error: showError, confirm } = useAlert();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [kycModal, setKycModal] = useState(null);

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    try {
      const data = await getIncomingRequests();
      setRequests(data || []);
    } catch (err) {
      showError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (request) => {
    const confirmed = await confirm(t('dropshipping.confirmApprove'));
    if (!confirmed) return;
    try {
      await reviewRequest(request.id, { decision: 'approved' });
      success(t('dropshipping.requestApproved'));
      loadRequests();
    } catch (err) {
      showError(err.message);
    }
  };

  const handleReject = async () => {
    if (!rejectModal) return;
    try {
      await reviewRequest(rejectModal.id, { decision: 'rejected', rejectionReason });
      success(t('dropshipping.requestRejected'));
      setRejectModal(null);
      setRejectionReason('');
      loadRequests();
    } catch (err) {
      showError(err.message);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>{t('dropshipping.requests')}</h1>
      </div>

      {loading ? (
        <div className={styles.loading}>{t('dropshipping.loading')}</div>
      ) : requests.length === 0 ? (
        <div className={styles.emptyState}>
          <p>{t('dropshipping.noRequestsFound')}</p>
          <small>{t('dropshipping.noRequestsDescription')}</small>
        </div>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('dropshipping.resellerName')}</th>
                <th>{t('dropshipping.contactPhone')}</th>
                <th>{t('dropshipping.requestedDate')}</th>
                <th>{t('dropshipping.proposedShopName')}</th>
                <th>{t('dropshipping.paymentCycleCol')}</th>
                <th>{t('dropshipping.statusCol')}</th>
                <th>{t('dropshipping.actionsCol')}</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((req) => {
                const cfg = STATUS_CONFIG[req.status] || STATUS_CONFIG.pending;
                const StatusIcon = cfg.icon;
                return (
                  <tr key={req.id}>
                    <td>{req.resellerBusiness?.name || '—'}</td>
                    <td>{req.resellerBusiness?.dropshippingSettings?.resellerContactPhone || '—'}</td>
                    <td>{formatDate(req.createdAt)}</td>
                    <td>{req.requestedShopName}</td>
                    <td>{req.requestedPaymentCycle} {t('dropshipping.days')}</td>
                    <td>
                      <span className={`${styles.statusBadge} ${styles[cfg.className]}`}>
                        <StatusIcon size={12} />
                        {t(`dropshipping.${req.status}`)}
                      </span>
                    </td>
                    <td>
                      <div className={styles.rowActions}>
                        <button className={styles.btnKyc} onClick={() => setKycModal(req)} title={t('dropshipping.viewKyc')}>
                          <ImageIcon size={14} />
                        </button>
                        {req.status === 'pending' && (
                          <>
                            <button className={styles.btnApprove} onClick={() => handleApprove(req)}>
                              <CheckCircle size={14} /> {t('dropshipping.approve')}
                            </button>
                            <button className={styles.btnReject} onClick={() => { setRejectModal(req); setRejectionReason(''); }}>
                              <XCircle size={14} /> {t('dropshipping.reject')}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {kycModal && (() => {
        const ds = kycModal.resellerBusiness?.dropshippingSettings;
        const images = [
          { key: 'kycNicFront', label: t('dropshipping.nicFront') },
          { key: 'kycNicBack', label: t('dropshipping.nicBack') },
          { key: 'kycBusinessRegImage', label: t('dropshipping.businessRegImage') },
        ].filter(({ key }) => ds?.[key]);
        return (
          <div className={styles.modalOverlay} onClick={() => setKycModal(null)}>
            <div className={`${styles.modal} ${styles.kycModal}`} onClick={(e) => e.stopPropagation()}>
              <h3>{t('dropshipping.kycDocuments')}: {kycModal.resellerBusiness?.name}</h3>
              {images.length === 0 ? (
                <p className={styles.kycNoImages}>{t('dropshipping.noKycImages')}</p>
              ) : (
                <div className={styles.kycImageGrid}>
                  {images.map(({ key, label }) => (
                    <div key={key} className={styles.kycImageItem}>
                      <span className={styles.kycImageLabel}>{label}</span>
                      <a href={getImageUrl(ds[key])} target="_blank" rel="noopener noreferrer">
                        <img
                          src={getImageUrl(ds[key])}
                          alt={label}
                          className={styles.kycImage}
                        />
                      </a>
                    </div>
                  ))}
                </div>
              )}
              <div className={styles.modalActions}>
                <button className={styles.btnOutline} onClick={() => setKycModal(null)}>{t('dropshipping.close')}</button>
              </div>
            </div>
          </div>
        );
      })()}

      {rejectModal && (
        <div className={styles.modalOverlay} onClick={() => setRejectModal(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3>{t('dropshipping.rejectRequest')}: {rejectModal.resellerBusiness?.name}</h3>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>{t('dropshipping.rejectionReason')}</label>
              <textarea
                className={styles.textarea}
                rows={4}
                placeholder={t('dropshipping.rejectionReasonPlaceholder')}
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
              />
            </div>
            <div className={styles.modalActions}>
              <button className={styles.btnOutline} onClick={() => setRejectModal(null)}>{t('dropshipping.cancel')}</button>
              <button className={styles.btnReject} onClick={handleReject}>{t('dropshipping.reject')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResellerRequestsPage;
